'use client';

import { useEffect, useRef } from 'react';
import Script from 'next/script';

declare global {
  interface Window {
    turnstile?: {
      render: (container: string | HTMLElement, options: TurnstileOptions) => string;
      reset: (widgetId?: string) => void;
      remove: (widgetId?: string) => void;
      getResponse: (widgetId?: string) => string;
    };
  }
}

interface TurnstileOptions {
  language?: 'es' | 'en' | 'auto';
  sitekey: string;
  callback?: (token: string) => void;
  'error-callback'?: () => void;
  'expired-callback'?: () => void;
  'timeout-callback'?: () => void;
  size?: 'normal' | 'compact' | 'flexible';
  theme?: 'auto' | 'light' | 'dark';
  appearance?: 'always' | 'execute' | 'interaction-only';
}

interface TurnstileWidgetProps {
  language?: 'es' | 'en' | 'auto';
  /** Callback con el token validado por Cloudflare */
  onToken: (token: string) => void;
  /**
   * Modo del widget:
   * - `'always'` (default): visible siempre, muestra "Verificando" → "Operación exitosa".
   *   OK para apartamentos donde el usuario espera anti-bot visible.
   * - `'interaction-only'`: invisible salvo que Cloudflare detecte sospecha.
   *   Recomendado para tours / checkout premium para no romper jerarquía visual
   *   con el banner "¡Operación exitosa!" del widget que se confunde con
   *   confirmación de pago.
   */
  appearance?: 'always' | 'interaction-only' | 'execute';
}

/**
 * Widget Turnstile. Renderiza solo si NEXT_PUBLIC_TURNSTILE_SITE_KEY existe.
 * El modo de challenge (invisible/visible) lo decide el dashboard de Cloudflare
 * (configurado en "Gestionado" para esta cuenta = invisible para usuarios
 * confiables, challenge mínimo para sospechosos).
 */
export default function TurnstileWidget({ onToken, appearance = 'always', language = 'auto' }: TurnstileWidgetProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const callbackRef = useRef(onToken);
  callbackRef.current = onToken;

  const siteKey = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY;

  useEffect(() => {
    if (!siteKey || !containerRef.current) return;

    let cancelled = false;
    let pollInterval: ReturnType<typeof setInterval> | null = null;

    const tryRender = () => {
      if (cancelled || !window.turnstile || !containerRef.current) return false;
      if (widgetIdRef.current) return true; // Ya renderizado

      try {
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          appearance,
          language,
          callback: (token: string) => {
            callbackRef.current(token);
          },
          'error-callback': () => {
            console.warn('[Turnstile] error-callback fired');
          },
          'expired-callback': () => {
            console.info('[Turnstile] token expired, resetting');
            if (widgetIdRef.current && window.turnstile) {
              window.turnstile.reset(widgetIdRef.current);
            }
          },
        });
        return true;
      } catch (err) {
        console.error('[Turnstile] render failed:', err);
        return false;
      }
    };

    // Intento inmediato si el script ya está cargado
    if (!tryRender()) {
      // Si no, polling cada 100ms hasta que window.turnstile esté disponible
      pollInterval = setInterval(() => {
        if (tryRender()) {
          if (pollInterval) clearInterval(pollInterval);
        }
      }, 100);
    }

    return () => {
      cancelled = true;
      if (pollInterval) clearInterval(pollInterval);
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          /* ignore */
        }
        widgetIdRef.current = null;
      }
    };
  }, [siteKey, appearance, language]);

  if (!siteKey) {
    return null;
  }

  return (
    <>
      <Script
        src="https://challenges.cloudflare.com/turnstile/v0/api.js"
        strategy="lazyOnload"
        async
        defer
      />
      <div ref={containerRef} className="turnstile-container" />
    </>
  );
}
